import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { removeFromHand, resetGameState } from '../../store/gameSlice';
import axios from '../../services/api-axios';
import { offlineThrowCard } from '../../services/offline/offlineGameController';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import Card from './Card';
import GameOverModal from '../GameOverModal/GameOverModal';
import RoundIndicator from './RoundIndicator';
import styles from './GameBoard.module.css';
import PlayerAvatar from '../common/PlayerAvatar';
import {
    playFeedback,
    isFeedbackMuted,
    setFeedbackMuted,
    startBackgroundMusic,
    stopBackgroundMusic,
} from '../../services/feedback';

const SUITS = ['♠', '♥', '♦', '♣'];

const isBotName = (name) => typeof name === 'string' && name.includes('#BOT#');
const initialOf = (name) => (isBotName(name) ? '🤖' : (name?.charAt(0)?.toUpperCase() || '?'));
const displayNameOf = (name) => (isBotName(name) ? 'Bot' : (name || 'Waiting...'));

const PlayerCard = ({ playerName, isCurrentTurn, team }) => {
    const [timeLeft, setTimeLeft] = useState(10);

    useEffect(() => {
        let interval;
        if (isCurrentTurn) {
            setTimeLeft(10);
            interval = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            setTimeLeft(10);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isCurrentTurn]);

    const radius = 24;
    const circumference = 2 * Math.PI * radius;
    const progress = timeLeft / 10;
    const ringColor = timeLeft > 5 ? '#10B981' : timeLeft > 2 ? '#F59E0B' : '#EF4444';
    const avatarClass = team === 'blue' ? styles.avatarTeamBlue : team === 'red' ? styles.avatarTeamRed : styles.avatarTeamNeutral;

    return (
        <motion.div
            className={styles.playerBadge}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            <div className={styles.avatarRingWrap}>
                <svg className={styles.avatarRingSvg} viewBox="0 0 56 56" width="100%" height="100%">
                    <circle className={styles.avatarRingTrack} cx="28" cy="28" r={radius} />
                    {isCurrentTurn && (
                        <motion.circle
                            className={styles.avatarRingProgress}
                            cx="28"
                            cy="28"
                            r={radius}
                            stroke={ringColor}
                            strokeDasharray={circumference}
                            initial={{ strokeDashoffset: 0 }}
                            animate={{ strokeDashoffset: circumference * (1 - progress) }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            style={{ filter: `drop-shadow(0 0 4px ${ringColor})` }}
                        />
                    )}
                </svg>
                <div className={`${styles.avatarCircle} ${avatarClass}`}>
                    <PlayerAvatar name={playerName} size={44} />
                </div>
                {isCurrentTurn && (
                    <motion.div
                        className={styles.turnBadgeDot}
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                    >
                        ⏱
                    </motion.div>
                )}
            </div>
            <span className={`${styles.playerName} ${isCurrentTurn ? styles.playerNameActive : ''}`}>
                {displayNameOf(playerName)}
            </span>
        </motion.div>
    );
};

const CONFETTI_COLORS = ['#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#8B5CF6'];

const Confetti = () => {
    const pieces = Array.from({ length: 14 }, (_, i) => i);
    return (
        <div className={styles.confettiField}>
            {pieces.map((i) => {
                const angle = (i / pieces.length) * 360;
                const distance = 60 + (i % 3) * 24;
                const rad = (angle * Math.PI) / 180;
                const x = Math.cos(rad) * distance;
                const y = Math.sin(rad) * distance;
                return (
                    <motion.span
                        key={i}
                        className={styles.confettiPiece}
                        style={{ background: CONFETTI_COLORS[i % CONFETTI_COLORS.length] }}
                        initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
                        animate={{ x, y: y + 50, opacity: 0, scale: 0.4, rotate: angle }}
                        transition={{ duration: 0.9, ease: 'easeOut' }}
                    />
                );
            })}
        </div>
    );
};

const CardBackStack = ({ count, orientation }) => {
    const maxCards = 4;
    const isHorizontal = orientation === 'horizontal';
    return (
        <div className={`${styles.cardBackRow} ${isHorizontal ? styles.cardBackRowHorizontal : styles.cardBackRowVertical}`}>
            <div className={`${styles.cardBackStack} ${isHorizontal ? styles.cardBackStackHorizontal : styles.cardBackStackVertical}`}>
                {count === 0 ? (
                    <span className={styles.cardBackEmpty}>empty</span>
                ) : (
                    Array.from({ length: Math.min(count, maxCards) }, (_, index) => (
                        <motion.div
                            key={index}
                            className={`${styles.cardBack} ${isHorizontal ? styles.cardBackHorizontal : styles.cardBackVertical}`}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: index * 0.06 }}
                            style={
                                isHorizontal
                                    ? { left: `${index * 10}px`, zIndex: index }
                                    : { top: `${index * 10}px`, zIndex: index }
                            }
                        />
                    ))
                )}
            </div>
            {count > 0 && <span className={styles.cardBackCount}>{count}</span>}
        </div>
    );
};

function GameBoard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const middle = useSelector((state) => state.game.middle);
    const hand = useSelector((state) => state.game.hand);
    const gameId = useSelector((state) => state.game.gameId);
    const isOffline = useSelector((state) => state.game.isOffline);
    const username = useSelector((state) => state.onlineUsers.username);
    const playerTurn = useSelector((state) => state.game.playerTurn);
    const somethingHappend = useSelector((state) => state.game.somethingHappend);
    const players = useSelector((state) => state.game.players);
    const gameSummary = useSelector((state) => state.gameSummary);

    const playerKeys = Object.keys(players);
    const team1 = [playerKeys[0], playerKeys[2]];
    const team2 = [playerKeys[1], playerKeys[3]];

    useEffect(() => {
        return () => {
            dispatch(resetGameState());
        };
    }, [dispatch]);

    const teamOf = (name) => {
        if (team1.includes(name)) return 'blue';
        if (team2.includes(name)) return 'red';
        return 'neutral';
    };

    // playerKeys is in turn order [T1P1, T2P1, T1P2, T2P2] (see getPlayers() in
    // usePrivateMessageHandler.ts), which is also seating order around the table -
    // each seat is a fixed rotation offset from the viewer, regardless of who's viewing.
    // (Four hardcoded per-idx branches used to live here; two of them referenced the
    // viewer's own seat index instead of an opponent's - always rotate with modulo math.)
    const getPlayerLayout = () => {
        const idx = playerKeys.indexOf(username);
        if (idx === -1) {
            return { sideR: "unknown", top: "unknown", sideL: "unknown" };
        }
        const seatAt = (offset) => {
            const key = playerKeys[(idx + offset) % 4];
            return key === undefined ? `#BOT#SEAT${offset}` : key;
        };
        return { sideR: seatAt(1), top: seatAt(2), sideL: seatAt(3) };
    };

    const layout = getPlayerLayout();

    const getInitialPosition = () => {
        if (layout.top === playerTurn) {
            return { opacity: 1, y: 0, x: 90 };
        } else if (layout.sideL === playerTurn) {
            return { opacity: 1, y: -90, x: 0 };
        } else if (layout.sideR === playerTurn) {
            return { opacity: 1, y: 90, x: 0 };
        } else if (username === playerTurn) {
            return { opacity: 1, y: 0, x: -90 };
        } else {
            return { opacity: 1, y: 130, x: 0 };
        }
    };

    const throwCard = (rank, suit) => {
        if (isOffline) {
            offlineThrowCard(dispatch, rank, suit);
            return;
        }
        axios.post(`/rest/game/${gameId}/throw`, {
            rank: rank,
            suit: suit
        }).then(res => {
            dispatch(removeFromHand({ rank, suit }));
        }).catch(error => {
            console.error(error);
        });
    };

    const handleLeaveGame = () => {
        if (!window.confirm('Leave this game?')) return;
        navigate(isOffline ? '/' : '/home');
    };

    const isMyTurn = username === playerTurn;

    const [muted, setMuted] = useState(isFeedbackMuted);
    const wasMyTurn = useRef(false);

    // Cue only on the transition into your turn, not on every re-render while it
    // is still your turn.
    useEffect(() => {
        if (isMyTurn && !wasMyTurn.current) playFeedback('yourTurn');
        wasMyTurn.current = isMyTurn;
    }, [isMyTurn]);

    // Ambient pad runs for the lifetime of the board only.
    useEffect(() => {
        startBackgroundMusic();
        return () => stopBackgroundMusic();
    }, []);

    const toggleMuted = () => {
        const next = !muted;
        setMuted(next);
        setFeedbackMuted(next);
        if (next) {
            stopBackgroundMusic();
        } else {
            startBackgroundMusic();
            playFeedback('yourTurn'); // preview what unmuting sounds like
        }
    };

    const eventClass = somethingHappend?.includes('ZNK')
        ? styles.eventNotificationZnk
        : somethingHappend?.includes('SLICE')
            ? styles.eventNotificationSlice
            : styles.eventNotificationDefault;

    const eventIcon = somethingHappend?.includes('ZNK')
        ? '⚡'
        : somethingHappend?.includes('SLICE')
            ? '🔥'
            : '🏁';

    return (
        <div className={styles.container}>
            <div className={styles.backgroundParticles} />

            <div className={styles.suitField} aria-hidden="true">
                {Array.from({ length: 10 }, (_, i) => (
                    <span
                        key={i}
                        className={styles.suit}
                        style={{
                            left: `${(i * 37) % 100}%`,
                            animationDuration: `${18 + (i % 5) * 4}s`,
                            animationDelay: `${-(i * 2.3)}s`,
                            fontSize: `${20 + (i % 4) * 10}px`
                        }}
                    >
                        {SUITS[i % SUITS.length]}
                    </span>
                ))}
            </div>

            <div className={styles.hud}>
                <div className={styles.hudLeft}>
                    <button className={styles.leaveButton} onClick={handleLeaveGame}>
                        ← Leave
                    </button>
                    <button
                        className={styles.leaveButton}
                        onClick={toggleMuted}
                        aria-pressed={!muted}
                        title={muted ? 'Enable sound and vibration' : 'Mute sound and vibration'}
                    >
                        {muted ? '🔇' : '🔊'}
                    </button>
                    <div className={styles.hudBrand}>
                        <span className={styles.hudBrandIcon}>🎴</span>
                        <span className={styles.hudBrandTag}>Do 101</span>
                    </div>
                </div>

                {isOffline && (
                    <div className={styles.scoreboard}>
                        <span className={styles.scoreboardTeam}>
                            <span className={styles.scoreboardDot} style={{ background: '#3B82F6' }} />
                            {gameSummary.team1Score.score}
                        </span>
                        <span className={styles.scoreboardDivider}>–</span>
                        <span className={styles.scoreboardTeam}>
                            {gameSummary.team2Score.score}
                            <span className={styles.scoreboardDot} style={{ background: '#EF4444' }} />
                        </span>
                    </div>
                )}

                <RoundIndicator />
            </div>

            <div className={styles.tableArea}>
                {/* Top Player */}
                <div className={styles.topPlayerSection}>
                    <PlayerCard
                        playerName={layout.top}
                        isCurrentTurn={layout.top === playerTurn}
                        team={teamOf(layout.top)}
                    />
                    <CardBackStack
                        count={players?.[layout?.top]?.cardCount || 0}
                        orientation="horizontal"
                    />
                </div>

                {/* Middle Row */}
                <div className={styles.middleRow}>
                    {/* Left Player */}
                    <div className={styles.sidePlayerSection}>
                        <PlayerCard
                            playerName={layout.sideL}
                            isCurrentTurn={layout.sideL === playerTurn}
                            team={teamOf(layout.sideL)}
                        />
                        <CardBackStack
                            count={players?.[layout?.sideL]?.cardCount || 0}
                            orientation="vertical"
                        />
                    </div>

                    {/* Middle Cards Area */}
                    <div className={styles.middleCardsArea}>
                        {middle.length === 0 && <div className={styles.tableWatermark}>🎴</div>}

                        {/* Middle pile: each card mounts once (stable key) and just settles in place
                            when a newer card lands on top of it — only a full pile clear (capture)
                            actually removes cards, which is when the exit/fly-away should play. */}
                        <AnimatePresence>
                            {middle.map((card, index) => {
                                const isTopCard = index === middle.length - 1;
                                return (
                                    <motion.div
                                        key={`${card.rank}-${card.suit}`}
                                        initial={isTopCard ? getInitialPosition() : { opacity: 1, x: 0, y: 0 }}
                                        animate={{ opacity: 1, y: 0, x: 0 }}
                                        exit={{ opacity: 0, y: -300, x: 0 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                        className={isTopCard ? styles.currentCardPosition : styles.cardPosition}
                                        style={{ transform: `rotate(${card.rotate || 0}deg)`, zIndex: index }}
                                    >
                                        <Card id={index} value={card.rank} suit={card.suit} angle={card.rotate} />
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>

                    {/* Right Player */}
                    <div className={styles.sidePlayerSection}>
                        <CardBackStack
                            count={players?.[layout?.sideR]?.cardCount || 0}
                            orientation="vertical"
                        />
                        <PlayerCard
                            playerName={layout.sideR}
                            isCurrentTurn={layout.sideR === playerTurn}
                            team={teamOf(layout.sideR)}
                        />
                    </div>

                    {/* Event banner: overlays the full table width (not just the narrow middle
                        box) so it's always centered on screen and never clipped on mobile. */}
                    <AnimatePresence>
                        {somethingHappend && (
                            <motion.div
                                key={somethingHappend}
                                className={styles.eventOverlay}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <motion.div
                                    initial={{ scale: 0.3, opacity: 0, rotate: -8 }}
                                    animate={{ scale: [0.3, 1.15, 1], opacity: 1, rotate: 0 }}
                                    exit={{ scale: 0.6, opacity: 0 }}
                                    transition={{ duration: 0.45, ease: 'easeOut' }}
                                    className={`${styles.eventNotification} ${eventClass}`}
                                >
                                    <span className={styles.eventIcon}>{eventIcon}</span>
                                    {somethingHappend}
                                </motion.div>
                                {somethingHappend?.includes('ZNK') && <Confetti />}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Player's Hand */}
                <div className={styles.bottomSection}>
                    {/* Always mounted (visibility toggled, not conditionally rendered) so the hand
                        row's height never changes with turn state and the table above doesn't shift. */}
                    <motion.div
                        animate={isMyTurn ? {
                            scale: [1, 1.05, 1],
                            boxShadow: [
                                '0 4px 20px rgba(16, 185, 129, 0.3)',
                                '0 8px 30px rgba(16, 185, 129, 0.5)',
                                '0 4px 20px rgba(16, 185, 129, 0.3)'
                            ]
                        } : {}}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={styles.yourTurnBadge}
                        style={{ visibility: isMyTurn ? 'visible' : 'hidden' }}
                    >
                        🎯 YOUR TURN
                    </motion.div>

                    <div className={styles.handCards}>
                        {hand.length === 0 && (
                            // Invisible placeholder: keeps this row's height pinned to a real
                            // card's size between rounds, when the hand is briefly empty, so
                            // the table above doesn't shift down and back up on redeal.
                            <div className={styles.handCard} style={{ visibility: 'hidden' }}>
                                <Card value="A" suit="spade" />
                            </div>
                        )}
                        {hand.map((card, index) => {
                            const mid = (hand.length - 1) / 2;
                            const offset = index - mid;
                            return (
                                <motion.div
                                    key={`${card.rank}-${card.suit}`}
                                    className={`${styles.handCard} ${isMyTurn ? styles.handCardActive : styles.handCardInactive}`}
                                    style={{ zIndex: index }}
                                    initial={{ rotate: offset * 5, y: Math.abs(offset) * 6, opacity: 0 }}
                                    animate={{ rotate: offset * 5, y: Math.abs(offset) * 6, opacity: 1 }}
                                    whileHover={{
                                        y: -16,
                                        rotate: 0,
                                        scale: 1.08,
                                        zIndex: 20,
                                        filter: 'drop-shadow(0 14px 20px rgba(0, 0, 0, 0.4))'
                                    }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => { if (isMyTurn) throwCard(card.rank, card.suit, index); }}
                                >
                                    <Card
                                        position="relative"
                                        id={index}
                                        value={card.rank}
                                        suit={card.suit}
                                    />
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <GameOverModal />
            <ToastContainer position="top-center" autoClose={3000} />
        </div>
    );
}

export default GameBoard;
